import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  FaCheckCircle,
  FaStar,
  FaArrowRight,
  FaTrophy,
  FaChartLine,
  FaBook
} from "react-icons/fa";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const NewStudentDashboard = () => {
  const [firstname, setFirstName] = useState("");
  const [userType, setUserType] = useState("");
  const [subjectsStatus, setSubjectsStatus] = useState([]);
  const [mockTestPackageAccess, setMockTestPackageAccess] = useState(false);
  const [individualSubjectAccess, setIndividualSubjectAccess] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [studentId, setStudentId] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);

  const subjects = [
    { key: 'Maths', name: 'Mathematical Reasoning Mock Test', displayName: 'Mathematical Reasoning Mock Test', price: 95 },
    { key: 'ThinkingSkills', name: 'Thinking Skills Mock Test', displayName: 'Thinking Skills Mock Test', price: 95 },
    { key: 'Writing', name: 'Writing Mock Test', displayName: 'Writing Mock Test', price: 95 },
    { key: 'Reading', name: 'Reading Mock Test', displayName: 'Reading Mock Test', price: 95 }
  ];

  useEffect(() => {
    const fetchData = async () => {
      const userData = localStorage.getItem("user");
      if (userData) {
        const user = JSON.parse(userData);
        setStudentId(user.id);
        setFirstName(user.firstName || user.username.split(" ")[0]);
        if (user.is_oc) {
          setUserType("oc");
        } else if (user.is_selective) {
          setUserType("selective");
        }

        try {
          // Check mock test package access
          const mockTestResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/mock-test/mocktest-package-access/${user.id}`
          );
          const mockTestData = await mockTestResponse.json();
          setMockTestPackageAccess(mockTestData.hasAccess);

          // Check individual subject access
          const accessPromises = subjects.map(async (subject) => {
            const response = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/mock-test/check-individual-access/${user.id}/${subject.key}`
            );
            const data = await response.json();
            return { [subject.key]: data.hasAccess };
          });

          const accessResults = await Promise.all(accessPromises);
          const accessMap = accessResults.reduce((acc, curr) => ({ ...acc, ...curr }), {});
          setIndividualSubjectAccess(accessMap);

        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, []);

  const practicePrice = "$109";

  // Handle package click - Direct navigation
  const handlePackageClick = async () => {
    if (mockTestPackageAccess) {
      // If purchased, go directly to mock tests page
      navigate('/mock-tests');
    } else {
      // If not purchased, go directly to Stripe payment
      await handleDirectPayment('package');
    }
  };

  // Handle subject click - Direct navigation
  const handleSubjectClick = async (subject) => {
    if (individualSubjectAccess[subject.key]) {
      // If purchased, go directly to test instructions
      navigate('/individual-mock-test', {
        state: {
          subject: subject.key,
          testType: 'individual',
          testName: subject.displayName,
          individualTest: true
        }
      });
    } else {
      // If not purchased, go directly to Stripe payment
      await handleDirectPayment('individual', subject);
    }
  };

  // Direct payment handler
  const handleDirectPayment = async (type, subject = null) => {
    if (!studentId) {
      alert("Please log in again");
      return;
    }

    setProcessingPayment(true);
    const stripe = await stripePromise;

    try {
      let endpoint;
      let body;

      if (type === 'package') {
        endpoint = "/api/payment/create-mocktest-package-session";
        body = { student_id: studentId };
      } else {
        endpoint = "/api/payment/create-individual-mocktest-session";
        body = {
          student_id: studentId,
          subject: subject.key
        };
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        const session = await response.json();
        const result = await stripe.redirectToCheckout({ sessionId: session.id });
        if (result.error) alert(result.error.message);
      } else {
        alert("Error creating payment session");
      }
    } catch (error) {
      console.error("Payment error:", error);
      alert("Payment failed. Please try again.");
    } finally {
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-teal-50 py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      {processingPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500 mx-auto"></div>
            <p className="mt-4 text-gray-700">Redirecting to payment...</p>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-2">
            Welcome back, <span className="text-teal-600">{firstname || "Student"}</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-600">Continue your learning journey</p>
        </div>

        <div className="space-y-6 sm:space-y-8">
          {/* Mock Test Package Card with Yellow/Orange Gradient Border */}
          <div className="group relative p-[2px] rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 transition-all duration-300 shadow-lg hover:shadow-xl">
            <div className="bg-white rounded-xl overflow-hidden">
              <div className="p-4 sm:p-6 lg:p-8">
                <div className="flex flex-col sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-4 lg:space-x-6">
                  <div className="bg-yellow-100 p-3 sm:p-4 rounded-full group-hover:bg-yellow-50 transition flex-shrink-0 self-center sm:self-start">
                    <FaStar className="text-yellow-500 text-2xl sm:text-3xl group-hover:text-yellow-600 transition" />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                      <div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 group-hover:text-gray-900 transition">
                          Mock Test Center
                        </h2>
                        <p className="text-sm sm:text-base text-gray-600 mt-1 group-hover:text-gray-700 transition">
                          Unlock your potential with our comprehensive mock test packages or choose individual subject mock tests tailored to your needs.
                          Our tests are deisgned to simulate real exam condition providing you with [erefect oppurtunityfir targeted practice and enhanced performance,
                          Start your journey to success today.
                        </p>
                      </div>
                    </div>

                    <ul className="mt-4 sm:mt-6 space-y-1 sm:space-y-2">
                      {/* 10 Complete Mock Tests Package - Direct Click */}
                      <li 
                        onClick={handlePackageClick}
                        className="flex items-start group cursor-pointer hover:bg-yellow-50 p-2 rounded-lg transition-colors"
                      >
                        <FaCheckCircle className={`mt-0.5 sm:mt-1 mr-2 flex-shrink-0 text-sm sm:text-base ${
                          mockTestPackageAccess ? 'text-green-500' : 'text-yellow-500'
                        }`} />
                        <span className="text-sm sm:text-base group-hover:text-gray-800 transition font-medium">
                          10 Complete Mock Tests Package ($450)
                          {mockTestPackageAccess ? (
                            <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                              Purchased - Click to start
                            </span>
                          ) : (
                            <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                              Click to buy
                            </span>
                          )}
                        </span>
                      </li>

                      {/* Individual Subject Mock Tests - Direct Click */}
                      {subjects.map((subject) => (
                        <li 
                          key={subject.key}
                          onClick={() => handleSubjectClick(subject)}
                          className="flex items-start group cursor-pointer hover:bg-yellow-50 p-2 rounded-lg transition-colors"
                        >
                          <FaCheckCircle className={`mt-0.5 sm:mt-1 mr-2 flex-shrink-0 text-sm sm:text-base ${
                            individualSubjectAccess[subject.key] ? 'text-green-500' : 'text-yellow-500'
                          }`} />
                          <span className="text-sm sm:text-base group-hover:text-gray-800 transition font-medium">
                            {subject.name} (${subject.price})
                            {individualSubjectAccess[subject.key] ? (
                              <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                Purchased - Click to start
                              </span>
                            ) : (
                              <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                                Click to buy
                              </span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* <div className="mt-6 sm:mt-8 flex justify-end">
                      <button
                        onClick={() => navigate('/test-history')}
                        className="text-blue-600 hover:text-blue-800 font-medium flex items-center group text-sm sm:text-base"
                      >
                        <span className="group-hover:underline">View Test History</span>
                        <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div> */}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Practice-a-thon Card with Blue/Purple Gradient Border */}
          <div className="group relative p-[2px] rounded-xl bg-gradient-to-br from-blue-400 to-purple-500 hover:from-blue-300 hover:to-purple-400 transition-all duration-300 shadow-lg hover:shadow-xl">
            <div className="bg-white rounded-xl overflow-hidden">
              <div className="p-4 sm:p-6 lg:p-8">
                <div className="flex flex-col sm:flex-row sm:items-start space-y-4 sm:space-y-0 sm:space-x-4 lg:space-x-6">
                  <div className="bg-blue-100 p-3 sm:p-4 rounded-full group-hover:bg-blue-50 transition flex-shrink-0 self-center sm:self-start">
                    <FaBook className="text-blue-500 text-2xl sm:text-3xl group-hover:text-blue-600 transition" />
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                      <div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 group-hover:text-gray-900 transition">
                          Practice-a-thon
                        </h2>
                        <p className="text-sm sm:text-base text-gray-600 mt-1 group-hover:text-gray-700 transition">
                          Unlock individual subjects and features
                        </p>
                      </div>
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-medium group-hover:bg-blue-50 group-hover:text-blue-700 transition mt-2 sm:mt-0">
                        {practicePrice}/per subject
                      </span>
                    </div>

                    <div className="mt-4 sm:mt-6">
                      <ul className="space-y-1 sm:space-y-2">
                        <li className="flex items-start group cursor-pointer hover:bg-blue-50 p-2 rounded-lg transition-colors">
                          <FaCheckCircle className="text-green-500 mt-0.5 sm:mt-1 mr-2 flex-shrink-0 text-sm sm:text-base" />
                          <span className="text-sm sm:text-base group-hover:text-gray-800 transition">Practice Tests</span>
                        </li>
                        <li className="flex items-start group cursor-pointer hover:bg-blue-50 p-2 rounded-lg transition-colors">
                          <FaTrophy className="text-yellow-500 mt-0.5 sm:mt-1 mr-2 flex-shrink-0 text-sm sm:text-base" />
                          <span className="text-sm sm:text-base group-hover:text-gray-800 transition">Badge Test</span>
                        </li>
                        <li className="flex items-start group cursor-pointer hover:bg-blue-50 p-2 rounded-lg transition-colors">
                          <FaChartLine className="text-blue-500 mt-0.5 sm:mt-1 mr-2 flex-shrink-0 text-sm sm:text-base" />
                          <span className="text-sm sm:text-base group-hover:text-gray-800 transition">Detailed Analytics</span>
                        </li>
                      </ul>
                    </div>

                    <div className="mt-6 sm:mt-8 flex justify-end">
                      <button
                        onClick={() => navigate('/subject-tests')}
                        className="text-blue-600 hover:text-blue-800 font-medium flex items-center group text-sm sm:text-base"
                      >
                        <span className="group-hover:underline">Explore Features</span>
                        <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewStudentDashboard;